import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { panel } from "../motion";
import { IconEdit, IconLog } from "./icons";

interface Anchor {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface RotaDayMenuProps {
  /** Day whose menu is open (YYYY-MM-DD), or null when closed. */
  date: string | null;
  /** Viewport rect of the clicked day cell, captured at click time. */
  anchor: Anchor | null;
  onEdit: (date: string) => void;
  onClose: () => void;
}

const GAP = 8;
const GUTTER = 12;

/**
 * Popup shown next to a rota day cell after clicking it — a context-menu
 * style popover anchored to the cell's rect, not a dropdown under the cell.
 * Offers Edit (open the day edit box) and Note (jump to that day's daily log).
 * Rendered fixed so it escapes .rota-card's overflow clip, then repositioned
 * once mounted to stay inside the viewport (flip to the left / clamp to the
 * bottom edge).
 */
export function RotaDayMenu({ date, anchor, onEdit, onClose }: RotaDayMenuProps) {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    if (!date || !anchor) return;
    function onDocDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onClose);
    // The calendar scrolls inside .content; close instead of floating over
    // the wrong cell when the page moves under the popup.
    window.addEventListener("scroll", onClose, true);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onClose);
      window.removeEventListener("scroll", onClose, true);
    };
  }, [date, anchor, onClose]);

  // Once mounted, clamp to the viewport: prefer the right of the cell, flip
  // to the left when it would overflow, clamp vertically.
  useEffect(() => {
    if (!date || !anchor) {
      setPos(null);
      return;
    }
    const el = menuRef.current;
    if (!el) return;
    const mw = el.offsetWidth;
    const mh = el.offsetHeight;
    let left = anchor.left + anchor.width + GAP;
    if (left + mw > window.innerWidth - GUTTER) left = anchor.left - mw - GAP;
    let top = anchor.top;
    if (top + mh > window.innerHeight - GUTTER) top = Math.max(GUTTER, window.innerHeight - mh - GUTTER);
    setPos({ left: Math.max(GUTTER, left), top: Math.max(GUTTER, top) });
  }, [date, anchor]);

  return (
    <AnimatePresence>
      {date && anchor ? (
        <motion.div
          key={date}
          ref={menuRef}
          className="rota-day-menu"
          role="menu"
          aria-label={`Actions for ${date}`}
          style={{ left: pos?.left ?? anchor.left + anchor.width + GAP, top: pos?.top ?? anchor.top }}
          variants={panel}
          initial="hidden"
          animate="show"
          exit="exit"
        >
          <button type="button" role="menuitem" className="dropdown-item" onClick={() => onEdit(date)}>
            <span className="menu-item-icon"><IconEdit size={16} /></span>
            Edit
          </button>
          <button type="button" role="menuitem" className="dropdown-item" onClick={() => navigate(`/log/${date}`)}>
            <span className="menu-item-icon"><IconLog size={16} /></span>
            Note
          </button>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
